import useScrollReveal from "@/hooks/useScrollReveal";

interface Reason {
  icon: string;
  title: string;
  description: string;
}

interface WhyChooseUsProps {
  tagline: string;
  title: string;
  subtitle: string;
  image: string;
  reasons: Reason[];
  ctaText: string;
  ctaHref: string;
}

export default function WhyChooseUs({
  tagline,
  title,
  subtitle,
  image,
  reasons,
  ctaText,
  ctaHref,
}: WhyChooseUsProps) {
  const contentRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="bg-white overflow-hidden">
      <div className="flex flex-col lg:flex-row" style={{ minHeight: "720px" }}>

        {/* ── Image — bleeds flush to left edge ───────────────────── */}
        <div className="relative lg:w-[54%] overflow-hidden" style={{ minHeight: "560px" }}>
          <img
            src={image}
            alt={title + " – Glen Davis and Associates Photography Bremerton WA"}
            title={title}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          {/* Subtle right-edge vignette that blends into white bg */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-white/[0.04]" />
          {/* Atmospheric bottom */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/15 via-transparent to-transparent" />

          {/* Phone badge — overlaps from right */}
          <div className="absolute bottom-10 -right-0 lg:right-0 z-20 hidden lg:flex flex-col items-start bg-charcoal text-white px-7 py-5 border-l-4 border-gold shadow-2xl">
            <div className="w-5 h-5 flex items-center justify-center mb-2 text-gold">
              <i className="ri-phone-fill text-base"></i>
            </div>
            <p className="font-display text-lg font-bold whitespace-nowrap leading-tight">360-204-4747</p>
            <p className="font-sans text-stone-400 text-[10px] uppercase tracking-widest mt-1 whitespace-nowrap">Call to Reserve</p>
          </div>
        </div>

        {/* ── Content — right side, generous room ─────────────────── */}
        <div
          ref={contentRef}
          className="sr-hidden lg:w-[46%] flex flex-col justify-center px-10 lg:px-16 xl:px-24 py-24"
        >
          <div className="flex items-center gap-5 mb-6">
            <span className="inline-block w-12 h-px bg-gradient-to-r from-transparent via-gold to-transparent" />
            <p className="text-gold font-sans text-[11px] font-bold uppercase tracking-[0.28em]">{tagline}</p>
          </div>

          <h2
            className="font-display text-charcoal font-bold mb-6 leading-[0.94]"
            style={{ fontSize: "clamp(32px, 4vw, 60px)" }}
          >
            {title}
          </h2>
          <p className="text-stone-500 font-sans text-base leading-relaxed mb-14">
            {subtitle}
          </p>

          <div className="flex flex-col gap-8 mb-14">
            {reasons.map((reason, i) => (
              <div key={i} className="flex items-start gap-5 group">
                <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center border border-stone-100 group-hover:border-gold/40 group-hover:bg-cream transition-all duration-300 text-gold">
                  <i className={`${reason.icon} text-base`}></i>
                </div>
                <div>
                  <h3 className="font-display text-charcoal text-lg font-bold mb-1.5 leading-tight">
                    {reason.title}
                  </h3>
                  <p className="text-stone-500 font-sans text-sm leading-relaxed">
                    {reason.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <a
            href={ctaHref}
            className="group inline-flex items-center gap-3 px-10 py-4 bg-charcoal text-white font-sans font-bold text-[11px] uppercase tracking-[0.22em] hover:bg-gold transition-all duration-300 whitespace-nowrap cursor-pointer self-start"
          >
            {ctaText}
            <div className="w-4 h-4 flex items-center justify-center group-hover:translate-x-0.5 transition-transform">
              <i className="ri-arrow-right-line"></i>
            </div>
          </a>
        </div>
      </div>
    </section>
  );
}
